// frontend/src/lib/coworkers.ts
//
// The coworkers, in one file. Every face and name on every screen reads
// from here, so the briefing, the record, the Explorer, the AI Manager and the
// Coworker Hub cannot disagree (plan 03 section 1).
//
// Six procurement coworkers, one per stage of the six-stage lifecycle
// (Discover, Define, Source, Evaluate, Negotiate, Manage), plus Sophie on the
// Supply Chain Desk. Megan is the orchestrator on the Command Briefing: she
// holds no skills and no stage, so she is exported as MEGAN beside the roster
// (bible section 3.1). The roster before the six-stage lifecycle is kept below as
// COWORKERS_FIVE, so a rollback is a one-line switch (plan 02 section 7).
//
// Role lines are frozen from screen v2-s14. Skills are the backend skill ids
// and are not renamed. Photos are in `public/coworkers/<key>.jpg`; a missing
// file falls back to a monogram on the stage colour.
//
// Tailwind class names are literal strings on purpose so the content scanner
// keeps them. Never build these by interpolation.

import { AI_EMPLOYEE_NOUN, PRODUCT_NOUN } from './naming'

export type CoworkerKey =
  | 'hannah'
  | 'ruby'
  | 'owen'
  | 'grace'
  | 'sam'
  | 'leo'
  | 'sophie'
  | 'megan'

/**
 * Stage keys a coworker, a graph node or a colour map can carry. The six-stage
 * keys are discover, define, source, evaluate, negotiate and manage. `procure`
 * and `award` stay for the five-stage build and for rows the backend wrote
 * under those names.
 */
export type CoworkerStage =
  | 'discover'
  | 'define'
  | 'source'
  | 'procure'
  | 'evaluate'
  | 'negotiate'
  | 'award'
  | 'manage'
  | 'supply'
  | 'orchestrator'

export interface CoworkerTone {
  /** Strong text colour. */
  text: string
  /** Light fill. */
  wash: string
  /** Pill and card border. */
  border: string
  /** Solid fill, for the monogram. */
  solid: string
  /** Ring around a face. */
  ring: string
}

export interface Coworker {
  key: CoworkerKey
  /** First name, as printed beside the face. */
  name: string
  stage: CoworkerStage
  /** "Define", "Supply Chain Desk". */
  stageLabel: string
  /** The persona PA gave this coworker: "Commercial analyst". */
  persona: string
  /** The role line from v2-s14, for cards and hovers. */
  role: string
  /** The column line on the Coworker Hub: "Define · Requirements and standards". */
  hubLine: string
  /** Backend skill ids and Sophie's SC skills. */
  skills: readonly string[]
  /** The gate this coworker brings to the viewer, in plain words. */
  gate: string
  /** Path under `public/`, without the base path. */
  photo: string
  tone: CoworkerTone
}

const TONES: Record<CoworkerStage, CoworkerTone> = {
  discover: {
    text: 'text-stage-discover',
    wash: 'bg-stage-discover-wash',
    border: 'border-stage-discover-border',
    solid: 'bg-stage-discover',
    ring: 'ring-stage-discover-border',
  },
  source: {
    text: 'text-stage-source',
    wash: 'bg-stage-source-wash',
    border: 'border-stage-source-border',
    solid: 'bg-stage-source',
    ring: 'ring-stage-source-border',
  },
  negotiate: {
    text: 'text-stage-negotiate',
    wash: 'bg-stage-negotiate-wash',
    border: 'border-stage-negotiate-border',
    solid: 'bg-stage-negotiate',
    ring: 'ring-stage-negotiate-border',
  },
  define: {
    text: 'text-stage-define',
    wash: 'bg-stage-define-wash',
    border: 'border-stage-define-border',
    solid: 'bg-stage-define',
    ring: 'ring-stage-define-border',
  },
  procure: {
    text: 'text-stage-procure',
    wash: 'bg-stage-procure-wash',
    border: 'border-stage-procure-border',
    solid: 'bg-stage-procure',
    ring: 'ring-stage-procure-border',
  },
  evaluate: {
    text: 'text-stage-evaluate',
    wash: 'bg-stage-evaluate-wash',
    border: 'border-stage-evaluate-border',
    solid: 'bg-stage-evaluate',
    ring: 'ring-stage-evaluate-border',
  },
  award: {
    text: 'text-stage-award',
    wash: 'bg-stage-award-wash',
    border: 'border-stage-award-border',
    solid: 'bg-stage-award',
    ring: 'ring-stage-award-border',
  },
  manage: {
    text: 'text-stage-manage',
    wash: 'bg-stage-manage-wash',
    border: 'border-stage-manage-border',
    solid: 'bg-stage-manage',
    ring: 'ring-stage-manage-border',
  },
  supply: {
    text: 'text-stage-supply',
    wash: 'bg-stage-supply-wash',
    border: 'border-stage-supply-border',
    solid: 'bg-stage-supply',
    ring: 'ring-stage-supply-border',
  },
  // Megan's tone is the brand navy (plan 07 section 10: colour #141A42).
  orchestrator: {
    text: 'text-brand-navy',
    wash: 'bg-brand-navy/5',
    border: 'border-brand-navy/20',
    solid: 'bg-brand-navy',
    ring: 'ring-brand-navy/30',
  },
}

export const COWORKERS: readonly Coworker[] = [
  {
    key: 'hannah',
    name: 'Hannah',
    stage: 'discover',
    stageLabel: 'Discover',
    persona: 'Commercial analyst',
    role: 'Contracts, spend, invoices and renewals',
    hubLine: 'Discover · Baseline and spend',
    skills: ['O1', 'O11', 'O12', 'DS1'],
    gate: 'Baseline and scope',
    photo: '/coworkers/hannah.jpg',
    tone: TONES.discover,
  },
  {
    key: 'ruby',
    name: 'Ruby',
    stage: 'define',
    stageLabel: 'Define',
    persona: 'Requirements lead',
    role: 'Requirements, specification and standards',
    hubLine: 'Define · Requirements and standards',
    skills: ['O2', 'O13', 'DF1', 'DF2', 'DF3'],
    gate: 'Requirements and standards',
    photo: '/coworkers/ruby.jpg',
    tone: TONES.define,
  },
  {
    key: 'owen',
    name: 'Owen',
    stage: 'source',
    stageLabel: 'Source',
    persona: 'Sourcing specialist',
    role: 'Route, tender pack and pricing template',
    hubLine: 'Source · Route and market',
    skills: ['O3', 'O4', 'O5', 'O14', 'O15', 'O16'],
    gate: 'Route, pack and criteria',
    photo: '/coworkers/owen.jpg',
    tone: TONES.source,
  },
  {
    key: 'grace',
    name: 'Grace',
    stage: 'evaluate',
    stageLabel: 'Evaluate',
    persona: 'Evaluation assurance',
    role: 'Compliance checks and bid comparison',
    hubLine: 'Evaluate · Comparison and compliance',
    skills: ['O6', 'O7', 'O17', 'EV1', 'O21'],
    gate: 'Scores and preferred bidder',
    photo: '/coworkers/grace.jpg',
    tone: TONES.evaluate,
  },
  {
    key: 'sam',
    name: 'Sam',
    stage: 'negotiate',
    stageLabel: 'Negotiate',
    persona: 'Commercial negotiator',
    role: 'Negotiation brief, positions and award',
    hubLine: 'Negotiate · Negotiation and award',
    skills: ['O8', 'O18', 'NG1', 'O20'],
    gate: 'Agreed position and award',
    photo: '/coworkers/sam.jpg',
    tone: TONES.negotiate,
  },
  {
    key: 'leo',
    name: 'Leo',
    stage: 'manage',
    stageLabel: 'Manage',
    persona: 'Contract manager',
    role: 'Commitments, invoices, KPIs and reviews',
    hubLine: 'Manage · Contract and performance',
    skills: ['MG1', 'O10', 'O9', 'O22', 'MG2', 'O19'],
    gate: 'Contractual action',
    photo: '/coworkers/leo.jpg',
    tone: TONES.manage,
  },
  {
    key: 'sophie',
    name: 'Sophie',
    stage: 'supply',
    stageLabel: 'Supply Chain Desk',
    persona: 'Supply chain desk',
    role: 'Requisitioner questions, orders and deliveries',
    hubLine: 'Supply Chain Desk',
    skills: ['SC1', 'SC2', 'SC3', 'SC4'],
    gate: 'Eastgate delivery trial',
    photo: '/coworkers/sophie.jpg',
    tone: TONES.supply,
  },
]

/**
 * The roster before the six-stage lifecycle: six coworkers, Hannah on Define.
 * Kept unchanged beside the new roster (plan 02 section 7).
 */
export const COWORKERS_FIVE: readonly Coworker[] = [
  {
    key: 'hannah',
    name: 'Hannah',
    stage: 'define',
    stageLabel: 'Define',
    persona: 'Commercial analyst',
    role: 'Business case, regime and route',
    hubLine: 'Define · Pipeline & Strategy',
    skills: ['O1', 'O2', 'O3', 'O11', 'O12', 'O13'],
    gate: 'Route and market approval',
    photo: '/coworkers/hannah.jpg',
    tone: TONES.define,
  },
  {
    key: 'owen',
    name: 'Owen',
    stage: 'procure',
    stageLabel: 'Procure',
    persona: 'Sourcing specialist',
    role: 'Tender pack and clarifications',
    hubLine: 'Procure · Market & Execution',
    skills: ['O4', 'O5', 'O14', 'O15', 'O16'],
    gate: 'Clarification approval',
    photo: '/coworkers/owen.jpg',
    tone: TONES.procure,
  },
  {
    key: 'grace',
    name: 'Grace',
    stage: 'evaluate',
    stageLabel: 'Evaluate',
    persona: 'Evaluation assurance',
    role: 'Compliance checks and scoring',
    hubLine: 'Evaluate · Scoring & Compliance',
    skills: ['O6', 'O7', 'O17'],
    gate: 'Evaluation lock',
    photo: '/coworkers/grace.jpg',
    tone: TONES.evaluate,
  },
  {
    key: 'sam',
    name: 'Sam',
    stage: 'award',
    stageLabel: 'Award',
    persona: 'Commercial negotiator',
    role: 'Award report, notices, contract',
    hubLine: 'Award · Award & Contract',
    skills: ['O8', 'O18'],
    gate: 'Award approval',
    photo: '/coworkers/sam.jpg',
    tone: TONES.award,
  },
  {
    key: 'leo',
    name: 'Leo',
    stage: 'manage',
    stageLabel: 'Manage',
    persona: 'Contract manager',
    role: 'KPIs, service credits, renewals',
    hubLine: 'Manage · Contract & Performance',
    skills: ['O9', 'O10', 'O19', 'O20', 'O21', 'O22'],
    gate: 'Post-award review',
    photo: '/coworkers/leo.jpg',
    tone: TONES.manage,
  },
  {
    key: 'sophie',
    name: 'Sophie',
    stage: 'supply',
    stageLabel: 'Supply Chain Desk',
    persona: 'Supply chain desk',
    role: 'Requisitioner questions, orders and deliveries',
    hubLine: 'Supply Chain Desk',
    skills: ['SC1', 'SC2', 'SC3', 'SC4'],
    gate: 'Eastgate delivery trial',
    photo: '/coworkers/sophie.jpg',
    tone: TONES.supply,
  },
]

/**
 * Megan, the procurement orchestrator (plan 11 A.2, F30). She watches every
 * contract and procurement, routes each stage and brings every decision to the viewer. No skills of her own and no
 * stage of the lifecycle, so she is not in COWORKERS (the hub columns and the
 * stage lookups read that list), but every face lookup finds her.
 */
export const MEGAN: Coworker = {
  key: 'megan',
  name: 'Megan',
  stage: 'orchestrator',
  stageLabel: 'Procurement orchestrator',
  persona: 'Procurement orchestrator',
  role: 'Watches every contract and procurement, hands each stage to the right coworker and brings every decision to you.',
  hubLine: 'Procurement orchestrator',
  skills: [],
  gate: 'Routes every decision to you',
  photo: '/coworkers/megan.jpg',
  tone: TONES.orchestrator,
}

/** Megan's kicker: "MEGAN · PROCUREMENT ORCHESTRATOR". */
export const MEGAN_KICKER = 'MEGAN · PROCUREMENT ORCHESTRATOR'

export const COWORKER_BY_KEY: Record<CoworkerKey, Coworker> = Object.fromEntries(
  [...COWORKERS, MEGAN].map((c) => [c.key, c])
) as Record<CoworkerKey, Coworker>

/** Total skills across the stage coworkers and Sophie (the hub header says 34). */
export const SKILL_COUNT = COWORKERS.reduce((n, c) => n + c.skills.length, 0)

/** Total skills in the five-stage roster (the hub header said 26). */
export const SKILL_COUNT_FIVE = COWORKERS_FIVE.reduce((n, c) => n + c.skills.length, 0)

/**
 * The stage key a name resolves to on the six-stage roster. The backend act id
 * "market" and the five-stage "procure" both read Source; "award" reads
 * Negotiate. Stage keys the roster does not know come back unchanged.
 */
export function sixStageKey(stage: string): string {
  if (stage === 'market' || stage === 'procure') return 'source'
  if (stage === 'award') return 'negotiate'
  return stage
}

/**
 * The coworker for a stage key. Accepts the six-stage keys and the older
 * names: "market" and "procure" read Owen (Source), "award" reads Sam
 * (Negotiate).
 */
export function coworkerForStage(stage: string): Coworker | undefined {
  const key = sixStageKey(stage)
  return COWORKERS.find((c) => c.stage === key)
}

/** The coworker for a stage key on the five-stage roster. Accepts the backend act id "market" for Procure. */
export function coworkerForStageFive(stage: string): Coworker | undefined {
  const key = stage === 'market' ? 'procure' : stage
  return COWORKERS_FIVE.find((c) => c.stage === key)
}

/** The coworker that owns a backend skill id on the five-stage roster ("O3" is Hannah's there). */
export function coworkerForSkillFive(skillId: string | null | undefined): Coworker | undefined {
  if (!skillId) return undefined
  return COWORKERS_FIVE.find((c) => c.skills.includes(skillId))
}

/** A coworker by first name, case-insensitive. Unknown names return undefined. */
export function coworkerByName(name: string | null | undefined): Coworker | undefined {
  const wanted = name?.trim().toLowerCase()
  if (!wanted) return undefined
  return [...COWORKERS, MEGAN].find((c) => c.name.toLowerCase() === wanted)
}

/** The coworker that owns a backend skill id such as "O6" or "SC3". */
export function coworkerForSkill(skillId: string | null | undefined): Coworker | undefined {
  if (!skillId) return undefined
  return COWORKERS.find((c) => c.skills.includes(skillId))
}

/** A coworker from a key or a first name, so callers can pass either. */
export function resolveCoworker(keyOrName: string | null | undefined): Coworker | undefined {
  if (!keyOrName) return undefined
  return COWORKER_BY_KEY[keyOrName.toLowerCase() as CoworkerKey] ?? coworkerByName(keyOrName)
}

/**
 * The caption under the name on a record card: "HANNAH · AI PROCUREMENT
 * COWORKER · DISCOVER". Pass AI_EMPLOYEE_NOUN for the five-stage wording.
 */
export function coworkerKicker(coworker: Coworker, noun: string = PRODUCT_NOUN): string {
  if (coworker.key === 'megan') return MEGAN_KICKER
  return `${coworker.name} · ${noun} · ${coworker.stageLabel}`.toUpperCase()
}

/** The five-stage kicker: "HANNAH · AI EMPLOYEE · DEFINE". */
export function coworkerKickerFive(coworker: Coworker): string {
  return coworkerKicker(coworker, AI_EMPLOYEE_NOUN)
}
